import type { FC, PropsWithChildren } from 'react';
import type { TCourseStream } from 'types/course';

import { useAppSelector } from 'hooks';
import React, { createContext, useCallback, useMemo, useState } from 'react';

export interface ICourseSwitchContextProps {
  courseValue: TCourseStream | null;
  onCourseValueUpdate: (updatedCourseValue: TCourseStream) => void;
}

export const CourseSwitchContext = createContext<ICourseSwitchContextProps>({
  courseValue: null,
  onCourseValueUpdate: () => null,
});

type TCourseValueLS = Record<string, TCourseStream> | null;

const CourseSwitcherProvider: FC<PropsWithChildren> = React.memo(({ children }) => {
  const storedCourseValue = useMemo(
    () =>
      JSON.parse(
        localStorage.getItem('current_course_stream') ?? `null`,
      ) as TCourseValueLS | null,
    [],
  );
  const userId = useAppSelector(storeState => storeState.auth.userId) ?? '';

  const [courseValue, setCourseValue] = useState<TCourseStream>(
    storedCourseValue?.[userId] ?? 'MATH',
  );

  const handleOnCourseValueUpdate = useCallback(
    (updatedCourseValue: TCourseStream) => {
      const payload = {
        ...storedCourseValue,
        [userId]: updatedCourseValue,
      };

      localStorage.setItem('current_course_stream', JSON.stringify(payload));

      setCourseValue(updatedCourseValue);
    },
    [storedCourseValue, userId],
  );

  const contextValue = useMemo(
    () => ({ courseValue, onCourseValueUpdate: handleOnCourseValueUpdate }),
    [courseValue, handleOnCourseValueUpdate],
  );

  return (
    <CourseSwitchContext.Provider value={contextValue}>
      {children}
    </CourseSwitchContext.Provider>
  );
});

export default CourseSwitcherProvider;
